import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import mapboxgl from 'mapbox-gl';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectRmHovered,
  selectRmOthersExpanded,
  setRmHovered,
} from 'state/routeMonitoringSlice';
import { selectCurrentSavedRoutes } from 'state/savedRouteSlices';
import {
  selectMaximize,
  selectRouteSelection,
  setMapCenter,
} from 'state/workflowSlice';

import { MapStyle } from 'appConstants';
import { routeSelectionIsSavedRoute } from 'features/common/hooks/useRouteInfo';
import { Map } from 'features/map/Map';
import { MapContext } from 'features/map/MapContext';
import { useHoverPulseAnimation } from 'features/map/hooks/useHoverPulseAnimation';
import { MapRouteTooltip } from 'features/map/tooltips/MapRouteTooltip';
import {
  jsonTupleToLatlng,
  lineMidStub,
} from 'features/map/utils/geography_utils';
import {
  addMapLayerInDefinedStack,
  getRouteInfoFromMouseEvent,
  mapHasLayer,
  mapHasSource,
  setHoverCursorTypeForHover,
} from 'features/map/utils/mapCommon';
import {
  getMonitorSymbolBgLayerDefinition,
  getMonitorSymbolLayerDefinition,
  getRouteMonitorBorderLayerDefinition,
  getRouteMonitorLayerDefinition,
} from 'features/map/utils/mapLayerDefinitions';
import { fitBoundsToBbox, isGeometryOnMap } from 'features/map/utils/mapUtils';
import {
  kMonitorRouteBorderLayerId,
  kMonitorRouteLayerId,
  kMonitorSymbolBgId,
  kMonitorSymbolId,
  selectedRouteColor,
  selectedSegmentColor,
} from 'features/map/utils/map_constants';
import { MaximizeOnlyButton } from 'features/workspace/MaximizeButton';
import { workflowStyles } from 'features/workspace/workspaceUtils';
import { debounce } from 'lodash';

interface IRouteMonitoringMap {
  extras?: ReactNode;
  setEnableHover: (v: boolean) => void;
}

export function RouteMonitoringMap({
  extras,
  setEnableHover,
}: IRouteMonitoringMap) {
  const { refMap, mapReady, refStyleReady, mapStyle } = useContext(MapContext);
  const dispatch = useAppDispatch();
  const [tooltipContent, setTooltipContent] = useState(null);
  const [pendingPanToRoute, setPendingPanToRoute] = useState<boolean>(false);
  const routes_all = useAppSelector(selectCurrentSavedRoutes);
  const routeSelection = useAppSelector(selectRouteSelection);
  const hoveredRouteId = useAppSelector(selectRmHovered);
  const maximize = useAppSelector(selectMaximize);
  const othersExapnded = useAppSelector(selectRmOthersExpanded);
  const { map_style } = workflowStyles(maximize);
  const lastHoveredRef = useRef<string | undefined>(hoveredRouteId);
  const createRouteMode = !!(
    routeSelection && !routeSelectionIsSavedRoute(routeSelection)
  );
  const debouncedDispatchHover = useMemo(
    () =>
      debounce((routeId) => {
        dispatch(setRmHovered(routeId));
      }, 50),
    [],
  );

  useEffect(
    () => () => debouncedDispatchHover.cancel(),
    [debouncedDispatchHover],
  );

  useEffect(() => {
    if (routeSelection) setPendingPanToRoute(true);
  }, [routeSelection]);

  const onMapMouseMoveHandler = useCallback(
    (event: mapboxgl.MapMouseEvent) => {
      let routeInfo;
      if (
        !createRouteMode &&
        mapHasLayer(refMap.current, kMonitorRouteLayerId)
      ) {
        routeInfo = getRouteInfoFromMouseEvent(
          refMap.current,
          event,
          kMonitorRouteLayerId,
        );
        if (routeInfo && routeInfo.properties) {
          const { route } = routeInfo.properties;
          setHoverCursorTypeForHover(refMap.current, true);
          if (lastHoveredRef.current !== route) {
            lastHoveredRef.current = route;
            setTooltipContent(routeInfo);
            debouncedDispatchHover(route);
          }
        } else if (lastHoveredRef.current !== undefined) {
          lastHoveredRef.current = undefined;
          setTooltipContent(undefined);
          setHoverCursorTypeForHover(refMap.current, false);
          debouncedDispatchHover(undefined);
        }
      }
    },
    [createRouteMode],
  );

  useEffect(() => {
    setEnableHover(createRouteMode || !tooltipContent);
  }, [tooltipContent, createRouteMode]);

  useEffect(() => {
    if (!mapReady || !pendingPanToRoute || !routes_all || !routeSelection)
      return;
    const selectedRoute = routes_all.find((el) => el.id === routeSelection);
    if (!selectedRoute || !refMap.current) return;
    const { feature, savedRoute } = selectedRoute;
    const centerPoint = savedRoute.centerPoint?.coordinates;
    if (centerPoint) {
      const center = jsonTupleToLatlng(centerPoint as any);
      dispatch(setMapCenter(center));
    }
    if (!isGeometryOnMap(feature.geometry, refMap.current))
      fitBoundsToBbox(refMap.current, feature.geometry, 0.33);
    setPendingPanToRoute(false);
  }, [mapReady, routeSelection, pendingPanToRoute, routes_all]);

  const memoizedData = useMemo(() => {
    if (!routes_all) return [];

    return routes_all
      .filter((el) => el.savedRoute.starred || othersExapnded)
      .map((route) => {
        const mid_point = lineMidStub(route.feature);

        return {
          routes: {
            type: 'Feature',
            geometry: route.feature.geometry,
            id: route.id,
            properties: {
              route_length: route.feature.properties.route_length,
              cardinal_direction: route.feature.properties.cardinal_direction,
              route: route.id,
              segmentId: route.savedRoute.realizedSegments.at(-1),
              name: route.savedRoute.routeName,
              isHovered:
                hoveredRouteId === route.savedRoute.id &&
                routeSelection !== route.savedRoute.id,
              isSelected: routeSelection === route.savedRoute.id,
            },
          },
          symbols: {
            ...mid_point,
            properties: { isStarred: route.savedRoute.starred },
          },
        };
      });
  }, [
    routes_all,
    othersExapnded,
    routeSelection,
    hoveredRouteId,
    selectedRouteColor,
    selectedSegmentColor,
  ]);

  const ensureBorderLayer = (map: mapboxgl.Map, mapStyle: MapStyle) => {
    if (!mapHasLayer(map, kMonitorRouteBorderLayerId)) {
      addMapLayerInDefinedStack(map, {
        ...getRouteMonitorBorderLayerDefinition(mapStyle),
        source: kMonitorRouteLayerId,
      } as mapboxgl.AnyLayer);
    }
  };

  const ensureRouteLayer = (map: mapboxgl.Map, mapStyle: MapStyle) => {
    if (!mapHasLayer(map, kMonitorRouteLayerId)) {
      addMapLayerInDefinedStack(map, {
        ...getRouteMonitorLayerDefinition(mapStyle),
        source: kMonitorRouteLayerId,
      } as mapboxgl.AnyLayer);
    }
  };

  const ensureSymbolLayerAndSource = (
    map: mapboxgl.Map,
    mapStyle: MapStyle,
  ) => {
    if (!mapHasSource(map, kMonitorSymbolId)) {
      map.addSource(kMonitorSymbolId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: memoizedData.map((el) => el.symbols) as any,
        },
      });
    } else {
      (map.getSource(kMonitorSymbolId) as any).setData({
        type: 'FeatureCollection',
        features: memoizedData.map((el) => el.symbols) as any,
      });
    }
    if (!mapHasLayer(map, kMonitorSymbolBgId)) {
      addMapLayerInDefinedStack(
        map,
        getMonitorSymbolBgLayerDefinition(mapStyle),
      );
    }
    if (!mapHasLayer(map, kMonitorSymbolId)) {
      addMapLayerInDefinedStack(map, getMonitorSymbolLayerDefinition(mapStyle));
    }
  };

  const redrawRoutes = () => {
    const map = refMap.current;
    if (!map || !mapReady || !refStyleReady.current) return;
    const routes = memoizedData.map((el) => el.routes) as any;
    if (!mapHasSource(map, kMonitorRouteLayerId)) {
      map.addSource(kMonitorRouteLayerId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: routes,
        },
      });
    } else {
      // to update data - otherwise flickering
      (map.getSource(kMonitorRouteLayerId) as any).setData({
        type: 'FeatureCollection',
        features: routes,
      });
    }
    ensureRouteLayer(map, mapStyle);
    ensureBorderLayer(map, mapStyle);
    ensureSymbolLayerAndSource(map, mapStyle);
  };

  useEffect(() => {
    if (
      memoizedData.length === 0 ||
      !refMap.current ||
      !refStyleReady.current ||
      !mapReady
    )
      return;
    redrawRoutes();
  }, [mapReady, memoizedData, routeSelection, hoveredRouteId]);

  useHoverPulseAnimation({
    hasData: memoizedData.length > 0,
    shouldStopAnimation: !hoveredRouteId && !routeSelection,
  });

  useEffect(() => {
    if (mapReady && refMap.current) {
      refMap.current.on('mousemove', onMapMouseMoveHandler);
    }
    return () => {
      if (refMap.current) {
        refMap.current.off('mousemove', onMapMouseMoveHandler);
      }
    };
  }, [mapReady, onMapMouseMoveHandler]);

  return (
    <div className={map_style}>
      <Map
        chartShowhide={false}
        showRoutes={false}
        enableRouteSelection
        routeLayerId={!createRouteMode && kMonitorRouteLayerId}
        extras={
          <>
            {extras}
            {tooltipContent && <MapRouteTooltip route={tooltipContent} />}
            {routes_all?.length > 0 && <MaximizeOnlyButton />}
          </>
        }
      />
    </div>
  );
}
